import { forwardRef, ReactNode, useCallback } from 'react';
import { Link, LinkProps, Navigate, NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface LinkWithSearchProps extends Omit<LinkProps, 'to'> {
  children: ReactNode;
  to: string;
  additionalParams?: Record<string, string | null | undefined>;
  paramKeysToClear?: string[];
}

/**
 * React router link that reserves all the current search params when navigating.
 * Preserves the same API interface as the regular Link component.
 * @example
 * <LinkWithSearch to="/path">Link</LinkWithSearch>
 * <LinkWithSearch to="/path" additionalParams={{ key: 'value' }}>Link with extra params</LinkWithSearch>
 */
export const LinkWithSearch = forwardRef<HTMLAnchorElement, LinkWithSearchProps>(
  ({ children, to, additionalParams, paramKeysToClear, ...props }, ref) => {
    const { search } = useLocation();

    const searchParams = updateSearchParams(search, additionalParams, paramKeysToClear);

    return (
      <StyledLink to={{ pathname: to, search: searchParams.toString() }} {...props} ref={ref}>
        {children}
      </StyledLink>
    );
  },
);
LinkWithSearch.displayName = 'LinkWithSearch';

const StyledLink = styled(Link)`
  color: rgb(3, 102, 214); // same as Primer's Link component
  :not(:hover) {
    text-decoration: none;
    color: inherit;
  }
`;

/**
 * Imperative counterpart of {@link LinkWithSearch}: navigates while keeping the
 * current search params. `options` takes the same shape react-router's
 * `navigate` accepts, so a redirect that should not add a history entry can pass
 * `{ replace: true }` and still keep the search string.
 *
 * Memoised, so callers may list it in a `useEffect` dependency array without the
 * effect re-running on every render.
 */
export function useNavigateWithSearch() {
  const navigate = useNavigate();
  const { search } = useLocation();

  return useCallback(
    (
      to: string,
      additionalParams?: Record<string, string | null | undefined>,
      paramKeysToClear?: string[],
      options?: NavigateOptions,
    ) => {
      const searchParams = updateSearchParams(search, additionalParams, paramKeysToClear);
      navigate(
        {
          pathname: to,
          search: searchParams.toString(),
        },
        options,
      );
    },
    [navigate, search],
  );
}

export const NavigateWithSearch = (props: {
  to: string;
  additionalParams?: Record<string, string | null | undefined>;
  paramKeysToClear?: string[];
}) => {
  const { search } = useLocation();
  const searchParams = updateSearchParams(search, props.additionalParams, props.paramKeysToClear);

  return <Navigate to={{ pathname: props.to, search: searchParams.toString() }} />;
};

function updateSearchParams(
  search?: string,
  additionalParams?: Record<string, string | null | undefined>,
  paramKeysToClear?: string[],
) {
  // Merge existing search params with additional params
  const searchParams = new URLSearchParams(search);

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value != null) {
        searchParams.set(key, value);
      }
    });
  }

  if (paramKeysToClear) {
    paramKeysToClear.forEach(param => {
      searchParams.delete(param);
    });
  }

  return searchParams;
}
